import React, { useState } from 'react';
import type { TierStatus } from '@/services/api/tier';
import { RefreshButton } from '@/components/ui/RefreshButton';

interface TierStatusBannerProps {
  tierStatus: TierStatus;
  onRefresh?: () => Promise<void> | void;
  onSwitchTier?: () => void;
  compact?: boolean;
  isRefreshing?: boolean;
}

/**
 * TierStatusBanner component displays the current tier status and spend information.
 * Shows as a collapsed card by default, expands on hover to show full details.
 * The expanded card overlays content below it to prevent layout shift.
 */
export const TierStatusBanner: React.FC<TierStatusBannerProps> = ({
  tierStatus,
  onRefresh,
  onSwitchTier,
  compact = false,
  isRefreshing = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLocalRefreshing, setIsLocalRefreshing] = useState(false);

  // Use either prop-based or local refreshing state
  const showRefreshing = isRefreshing || isLocalRefreshing;

  const handleRefresh = async () => {
    if (!onRefresh || showRefreshing) return;
    setIsLocalRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsLocalRefreshing(false);
    }
  };

  const {
    tier_type,
    status,
    total_spend_30_day,
    total_spend_365_day,
    account_count,
    required_spend,
    spend_period: spendPeriod,
    days_until_warning_expires,
    disconnected_accounts,
    days_until_disconnected_ends,
    days_until_grace_ends,
  } = tierStatus;

  const currentSpend = tier_type === 'standard' ? total_spend_30_day : total_spend_365_day;

  const spendPeriodLabel = tier_type === 'standard' ? 'Monthly' : 'Yearly';

  // Determine banner style based on status
  const getBannerStyle = () => {
    switch (status) {
      case 'active':
        return 'tier-card--success';
      case 'grace':
        return 'tier-card--info';
      case 'warning':
        return 'tier-card--warning';
      case 'disconnected':
        return 'tier-card--error';
      case 'pending':
      default:
        return 'tier-card--neutral';
    }
  };

  // Get display label for status badge
  const getStatusLabel = () => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'grace':
        return 'Grace Period';
      case 'warning':
        return 'Warning';
      case 'disconnected':
        return 'Disconnected';
      case 'pending':
        // Phase 3: pending means "no linked accounts." Below-threshold-with-
        // accounts now lives in grace / warning states, not pending.
        return 'Pending Ads Account Link';
      default:
        return String(status).charAt(0).toUpperCase() + String(status).slice(1);
    }
  };

  // Get status message - returns JSX to support links.
  // Countdown checks use `!= null` (not truthy) so the 0-day case still shows
  // the urgent countdown text instead of dropping to the fallback message.
  const getStatusMessage = (): React.ReactNode => {
    switch (status) {
      case 'active':
        return `${spendPeriodLabel} required spend has been satisfied.`;
      case 'grace':
        return days_until_grace_ends != null
          ? `You have ${days_until_grace_ends} days remaining in your grace period without spend requirements.`
          : 'You are in your grace period without spend requirements.';
      case 'warning':
        return days_until_warning_expires != null
          ? `Warning: Your spend is too low. You have ${days_until_warning_expires} days to restore access before accounts are unlinked.`
          : 'Warning: Your spend is too low.';
      case 'disconnected':
        return (
          <>
            {disconnected_accounts && disconnected_accounts.length > 0 ? (
              <>
                The following account(s) were unlinked due to low spend:{' '}
                <strong>{disconnected_accounts.map(a => a.account_name || a.account_id).join(', ')}</strong>.
              </>
            ) : (
              'One or more of your Google Ads accounts have been unlinked due to low spend.'
            )}
            {days_until_disconnected_ends != null && (
              <> You have {days_until_disconnected_ends} days to increase spend on remaining accounts.</>
            )}
          </>
        );
      case 'pending':
        // Phase 3: pending means exactly account_count === 0.
        return 'Connect a Google Ads account with sufficient spend to activate.';
      default:
        return '';
    }
  };

  // Calculate progress percentage
  const progressPercent = Math.min((currentSpend / required_spend) * 100, 100);

  if (compact) {
    return (
      <div className={`tier-banner tier-banner--compact ${getBannerStyle()}`}>
        <div className="tier-banner__compact-content">
          <span className="tier-banner__status-badge">{getStatusLabel()}</span>
          <span className="tier-banner__spend-info">
            ${currentSpend.toFixed(2)} / ${required_spend.toFixed(2)}
          </span>
          {status === 'warning' && days_until_warning_expires && (
            <span className="tier-banner__warning-days">
              {days_until_warning_expires} days left
            </span>
          )}
        </div>
        <style>{compactStyles}</style>
      </div>
    );
  }

  return (
    <div
      className="tier-card-wrapper"
      role="button"
      tabIndex={0}
      aria-expanded={isHovered}
      aria-label={`${tier_type === 'standard' ? 'Standard' : 'Seasonal'} Plan details`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => setIsHovered((prev) => !prev)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsHovered((prev) => !prev); } }}
    >
      {/* Collapsed state - always visible, determines the space taken */}
      <div className={`tier-card tier-card--collapsed ${getBannerStyle()}`}>
        <div className="tier-card__header">
          <h3 className="tier-card__title">
            {tier_type === 'standard' ? 'Standard' : 'Seasonal'} Plan
          </h3>
          <span className={`tier-card__status tier-card__status--${status}`}>
            {getStatusLabel()}
          </span>
        </div>
      </div>

      {/* Expanded state - overlays content below */}
      <div className={`tier-card tier-card--expanded ${getBannerStyle()} ${isHovered ? 'tier-card--visible' : ''}`}>
        <div className="tier-card__header">
          <h3 className="tier-card__title">
            {tier_type === 'standard' ? 'Standard' : 'Seasonal'} Plan
          </h3>
          <span className={`tier-card__status tier-card__status--${status}`}>
            {getStatusLabel()}
          </span>
        </div>

        <p className="tier-card__message">{getStatusMessage()}</p>

        <div className="tier-card__stats">
          <div className="tier-card__stat">
            <span className="tier-card__stat-label">Current Spend<br className="sm:hidden" /> ({spendPeriod})</span>
            <span className="tier-card__stat-value">${currentSpend.toFixed(2)}</span>
          </div>
          <div className="tier-card__stat">
            <span className="tier-card__stat-label">Required Spend</span>
            <span className="tier-card__stat-value">${required_spend.toFixed(2)}</span>
          </div>
          <div className="tier-card__stat">
            <span className="tier-card__stat-label">Linked Accounts</span>
            <span className="tier-card__stat-value">{account_count}</span>
          </div>
        </div>

        <div className="tier-card__progress-container">
          <div className="tier-card__progress-bar">
            <div
              className="tier-card__progress-fill"
              style={{ width: `${isNaN(progressPercent) ? 0 : progressPercent}%` }}
            />
          </div>
          <span className="tier-card__progress-text">
            {isNaN(progressPercent) ? '--% of threshold' : `${progressPercent.toFixed(0)}% of threshold`}
          </span>
        </div>

        {(onRefresh || onSwitchTier) && (
          <div className="tier-card__actions">
            {onRefresh && (
              <RefreshButton
                isRefreshing={showRefreshing}
                onClick={(e) => { e.stopPropagation(); handleRefresh(); }}
                size="sm"
              />
            )}
            {onSwitchTier && (
              <button
                type="button"
                className="tier-card__button tier-card__button--primary"
                onClick={(e) => { e.stopPropagation(); onSwitchTier?.(); }}
              >
                Switch Plan
              </button>
            )}
          </div>
        )}
      </div>

      <style>{cardStyles}</style>
    </div>
  );
};

const compactStyles = `
  .tier-banner--compact {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  .tier-banner__compact-content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .tier-banner__status-badge {
    font-weight: 500;
  }

  .tier-banner__spend-info {
    color: inherit;
  }

  .tier-banner__warning-days {
    font-weight: 600;
    color: inherit;
  }
`;

const cardStyles = `
  .tier-card-wrapper {
    position: relative;
    cursor: pointer;
  }

  .tier-card {
    border-radius: 0.75rem;
    border: 1px solid;
  }

  .tier-card--collapsed {
    padding: 0.75rem 1rem;
  }

  .tier-card--expanded {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    padding: 1rem;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px);
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .tier-card--expanded.tier-card--visible {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .tier-card--success {
    background: var(--green-50, #f0fdf4);
    border-color: var(--green-200, #bbf7d0);
    color: var(--green-800, #166534);
  }

  .tier-card--info {
    background: var(--blue-50, #eff6ff);
    border-color: var(--blue-200, #bfdbfe);
    color: var(--blue-800, #1e40af);
  }

  .tier-card--warning {
    background: var(--yellow-50, #fefce8);
    border-color: var(--yellow-200, #fef08a);
    color: var(--yellow-800, #854d0e);
  }

  .tier-card--error {
    background: var(--red-50, #fef2f2);
    border-color: var(--red-200, #fecaca);
    color: var(--red-800, #991b1b);
  }

  .tier-card--neutral {
    background: var(--gray-50, #f9fafb);
    border-color: var(--gray-200, #e5e7eb);
    color: var(--gray-800, #1f2937);
  }

  .tier-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .tier-card__title {
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 0;
    color: inherit;
  }

  .tier-card__status {
    padding: 0.2rem 0.6rem;
    border-radius: 9999px;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .tier-card__status--active {
    background: var(--green-100, #dcfce7);
    color: var(--green-700, #15803d);
  }

  .tier-card__status--grace {
    background: var(--blue-100, #dbeafe);
    color: var(--blue-700, #1d4ed8);
  }

  .tier-card__status--warning {
    background: var(--yellow-100, #fef9c3);
    color: var(--yellow-700, #a16207);
  }

  .tier-card__status--disconnected {
    background: var(--red-100, #fee2e2);
    color: var(--red-700, #b91c1c);
  }

  .tier-card__status--pending {
    background: var(--gray-100, #f3f4f6);
    color: var(--gray-700, #374151);
  }

  .tier-card__message {
    font-size: 0.8125rem;
    margin: 0.75rem 0;
    opacity: 0.9;
    line-height: 1.4;
  }

  .tier-card__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .tier-card__stat {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .tier-card__stat-label {
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 0.6875rem;
    opacity: 0.7;
  }

  .tier-card__stat-value {
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .tier-card__progress-container {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .tier-card__progress-bar {
    flex: 1;
    height: 6px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 9999px;
    overflow: hidden;
  }

  .tier-card__progress-fill {
    height: 100%;
    background: currentColor;
    border-radius: 9999px;
    transition: width 0.3s ease;
  }

  .tier-card__progress-text {
    font-size: 0.6875rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .tier-card__actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }

  .tier-card__button {
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .tier-card__button--primary {
    background: currentColor;
    color: white;
  }

  .tier-card__button--primary:hover {
    opacity: 0.9;
  }

  .tier-card__button:disabled {
    cursor: not-allowed;
  }

  .tier-card__link {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }

  .tier-card__link:hover {
    opacity: 0.8;
  }
`;

export default TierStatusBanner;
